<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { companySizeRange } from '$lib/stores/selection';

  /**
   * Embedded in a step that has already asked the question.
   *
   * The guide used four size bands here — micro, small, medium, large — on the
   * reasoning that nobody should have to decide whether "small" stops at nine
   * employees or ten. But it meant the guide offered less than the panel it
   * hands over to, and a customer who picked a band saw a different control the
   * moment they left the guide. Same slider, both places.
   */
  export let embedded = false;

  let useSizeFilter = false;

  /**
   * Whether the customer has moved anything.
   *
   * Embedded there is no checkbox to switch the filter on, so without this the
   * step would either write nothing ever or write 0–1000 on arrival — a filter
   * that excludes nobody, shown as a chip, counted as a choice. The range is
   * written once something is actually dragged or typed.
   */
  let touched = false;

  const min = 0;
  const max = 1000;
  const step = 10;

  let minValue = min;
  let maxValue = max;
  let sliderElement: HTMLElement | undefined;
  let isDraggingMin = false;
  let isDraggingMax = false;

  // Handle checkbox toggle
  function toggleSizeFilter() {
    useSizeFilter = !useSizeFilter;
    if (!useSizeFilter) {
      companySizeRange.set([null, null]);
      minValue = min;
      maxValue = max;
    } else {
      companySizeRange.set([minValue, maxValue]);
    }
  }

  // Sync checkbox when filter is changed externally (from filter pills or AI)
  $: if ($companySizeRange[0] === null && $companySizeRange[1] === null) {
    useSizeFilter = false;
    minValue = min;
    maxValue = max;
  } else if ($companySizeRange[0] !== null || $companySizeRange[1] !== null) {
    useSizeFilter = true;
    if ($companySizeRange[0] !== null) minValue = $companySizeRange[0];
    if ($companySizeRange[1] !== null) maxValue = $companySizeRange[1];
  }

  // Handle slider drag events
  function handleMouseMove(e: MouseEvent) {
    if (!isDraggingMin && !isDraggingMax) return;

    if (!sliderElement) return;

    const rect = sliderElement.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const value = Math.round(((percent / 100) * (max - min)) / step) * step + min;

    if (isDraggingMin && value <= maxValue - step) {
      minValue = value;
    } else if (isDraggingMax && value >= minValue + step) {
      maxValue = value;
    }
  }

  function handleMouseUp() {
    isDraggingMin = false;
    isDraggingMax = false;
  }

  // Update store when slider values change (only if filter is active)
  $: if (useSizeFilter || (embedded && touched)) {
    companySizeRange.set([minValue, maxValue]);
  }
</script>

<svelte:window on:mousemove={handleMouseMove} on:mouseup={handleMouseUp} />

<div class={embedded ? '' : useSizeFilter ? 'm-2 rounded-control bg-surface-well p-3' : 'p-3'}>
  {#if !embedded}
    <div class="mb-3">
      <label class="group flex cursor-pointer items-center">
        <input
          type="checkbox"
          checked={useSizeFilter}
          on:click={toggleSizeFilter}
          class="mr-2 h-3 w-3 rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200"
        />
        <svg class="filter-mark mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <span
          class="text-xs font-semibold text-gray-900 transition-colors group-hover:text-gray-700"
        >
          {$_('filters.size.employees')}
        </span>
      </label>
    </div>
  {/if}

  {#if useSizeFilter || embedded}
    <div class="w-full space-y-4">
      <!-- Custom Slider -->
      <div class="relative mb-6">
        <!-- Slider track -->
        <div class="relative h-1 rounded-full bg-gray-200" bind:this={sliderElement}>
          <!-- Active range -->
          <div
            class="absolute h-full rounded-full bg-accent-500"
            style="left: {(minValue / max) * 100}%; width: {((maxValue - minValue) / max) * 100}%"
          ></div>

          <!-- Min handle -->
          <div
            class="absolute top-1/2 -translate-y-1/2 cursor-grab"
            style="left: {(minValue / max) * 100}%"
            on:mousedown={(e) => {
              e.preventDefault();
              isDraggingMin = true;
              touched = true;
            }}
            role="slider"
            tabindex="0"
            aria-label={$_('filters.capital.from')}
            aria-valuemin={min}
            aria-valuemax={maxValue - step}
            aria-valuenow={minValue}
          >
            <div
              class="h-4 w-4 -translate-x-1/2 rounded-full bg-white shadow-raised ring-2 ring-accent-500 transition-colors"
            ></div>
            <div
              class="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 rounded bg-gray-800 px-2 py-0.5 text-xs text-white opacity-0 transition-opacity {isDraggingMin
                ? 'opacity-100'
                : ''}"
            >
              {minValue}
            </div>
          </div>

          <!-- Max handle -->
          <div
            class="absolute top-1/2 -translate-y-1/2 cursor-grab"
            style="left: {(maxValue / max) * 100}%"
            on:mousedown={(e) => {
              e.preventDefault();
              isDraggingMax = true;
              touched = true;
            }}
            role="slider"
            tabindex="0"
            aria-label={$_('filters.capital.to')}
            aria-valuemin={minValue + step}
            aria-valuemax={max}
            aria-valuenow={maxValue}
          >
            <div
              class="h-4 w-4 -translate-x-1/2 rounded-full bg-white shadow-raised ring-2 ring-accent-500 transition-colors"
            ></div>
            <div
              class="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 rounded bg-gray-800 px-2 py-0.5 text-xs text-white opacity-0 transition-opacity {isDraggingMax
                ? 'opacity-100'
                : ''}"
            >
              {maxValue}
            </div>
          </div>
        </div>

        <!-- Reference points -->
        <div class="mt-2 flex justify-between text-2xs text-gray-500">
          <span>0</span>
          <span>250</span>
          <span>500</span>
          <span>750</span>
          <span>1000+</span>
        </div>
      </div>

      <div class="flex gap-3 {embedded ? 'max-w-xs' : ''}">
        <div class="flex-1">
          <label class="mb-1 block text-gray-500 {embedded ? 'text-sm' : 'text-xs'}"
            >{$_('filters.capital.from')}</label
          >
          <input
            type="number"
            on:input={() => (touched = true)}
            bind:value={minValue}
            {min}
            max={maxValue - step}
            {step}
            class="w-full rounded-control border border-gray-300 px-2 py-1 text-center focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 {embedded
              ? 'text-sm'
              : 'text-xs'}"
          />
        </div>
        <div class="flex-1">
          <label class="mb-1 block text-gray-500 {embedded ? 'text-sm' : 'text-xs'}"
            >{$_('filters.capital.to')}</label
          >
          <input
            type="number"
            on:input={() => (touched = true)}
            bind:value={maxValue}
            min={minValue + step}
            {max}
            {step}
            class="w-full rounded-control border border-gray-300 px-2 py-1 text-center focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 {embedded
              ? 'text-sm'
              : 'text-xs'}"
          />
        </div>
      </div>
    </div>
  {/if}
</div>
